import ClientApi from '../../apis/client';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DealObject } from '../../params/pipeline';
import { errorHandler } from '../../helper/error_handler';
import { generateProspectResultMeta } from '../../helper/generate_prospect_meta';
import { isErrorWithMessage } from '../../helper';
import { resettingClientLoader } from '../slices/client';
import { RootState } from '../../redux/store';
import { settingSelectedClientDeal } from '../slices/deal';
import { AddCompanyParams, AddProjectParams, AddRemoveClientParams, ClientObject, UpdateClientDetailsParams } from '../../params/redux/client';

interface ClientResponse {
    success?: 0 | 1;
    msg?: string;
    clients?: ClientObject[];
    client_details?: ClientObject;
}

export const fetchAllClientsThunk = createAsyncThunk(
    'client/fetch-all',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: ClientResponse = await ClientApi.fetchClients(state.app.token);
            if (response?.success === 1) {
                return response?.clients;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);

export const fetchClientDetailThunk = createAsyncThunk(
    'client/fetch-detail',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: ClientResponse = await ClientApi.fetchClientDetail(state.app.token, id);
            if (response?.success === 1) {
                const deal = response?.client_details?.deal;
                if (deal) {
                    const updatedDeal = { ...deal, prospect_result_meta: generateProspectResultMeta(deal as DealObject) };
                    dispatch(settingSelectedClientDeal(updatedDeal));
                }
                return response?.client_details;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);

export const createCompanyThunk = createAsyncThunk(
    'client/create',
    async (data: AddCompanyParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ClientApi.createCompany(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchAllClientsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);

export const createprojectThunk = createAsyncThunk(
    'client/create-project',
    async (data: AddProjectParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ClientApi.createProject(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchAllClientsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);

export const addRemoveClientAssigneeThunk = createAsyncThunk(
    'client/add-remove-assignee',
    async (data: AddRemoveClientParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ClientApi.addRemoveAssigneeOfClient(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchAllClientsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);

export const updateClientDetailsThunk = createAsyncThunk(
    'client/update',
    async (data: UpdateClientDetailsParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ClientApi.updateClientDetails(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchClientDetailThunk(data.client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);

export const remindThemThunk = createAsyncThunk(
    'client/remind',
    async ({ client_id, type } : {client_id: number, type: 'Email' | 'Twilio'}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ClientApi.remindThem(state.app.token, client_id, type);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingClientLoader());
        }
    }
);